<template>
  <!-- NEW SIDE PANEL -->
  <div id="side-panel" :class="smallTabletBreakpoint" class="flex-col gap-12" :style="{ minWidth: panelWidth }">
    <!-- Start ToolBox -->
    <div :class="panelManagerWrapperStyles">
      <!-- Panel manager for desktop -->
      <PanelManager
        v-if="!isSmallTablet"
        :current-persistent-store="currentPersistentStore"
        :restore-original-result="restoreOriginalResult"
        :editor="editor"
        :toggle-panel="togglePanel"
        :stage="stage"
        :scale="initialScale"
        :getDataURL="props.getDataURL"
        :refreshAllHdLayers="props.refreshAllHdLayers"
        :stageHd="stageHd"
      />
    </div>
    <!-- End ToolBox -->

    <!-- Start Buttons - Panel Side Menu -->
    <div v-if="enableUI" class="flex flex-col items-start gap-3 toolbox-btns">
      <ActionButtonPlaceholder v-if="props.isProcessing"></ActionButtonPlaceholder>
      <!-- Background Button -->
      <ActionButton v-else @click="props.togglePanel('addBackground')">
        <template #icon>
          <AddIcon
            v-if="
              !props.currentPersistentStore?.selectedBackgroundPhotoUrl &&
              !props.currentPersistentStore?.selectedBackgroundColor
            "
          />
          <span v-else class="w-full h-full" :style="props.backgroundActionButtonStyle" />
        </template>
        <template #title>{{ I18n.t(`editor.background`) }}</template>
      </ActionButton>
      <ActionButtonPlaceholder v-if="props.isProcessing"></ActionButtonPlaceholder>
      <!-- Magic Brush Button -->
      <span
        v-if="hasExceededMagicBrushIterations(store.selectedImage, props.refreshTippy)"
        class="hover-tooltip"
        :data-tippy-content="`${I18n.t('ai_brush.error.iterations_exceeded')}`"
        data-tippy-placement="bottom"
      >
        <ActionButton
          v-if="!props.isProcessing"
          :disabled="hasExceededMagicBrushIterations(store.selectedImage, props.refreshTippy)"
        >
          <template #icon>
            <BrushIcon />
          </template>
          <template #title>{{ I18n.t(`editor.erase_restore`) }} </template>
        </ActionButton>
      </span>
      <!-- Erase/Restore Button -->
      <template v-else>
        <ActionButton
          v-if="!props.isProcessing"
          @click="props.togglePanel('eraseRestore')"
          :disabled="hasExceededMagicBrushIterations(store.selectedImage, props.refreshTippy)"
        >
          <template #icon>
            <BrushIcon />
          </template>
          <template #title>{{ I18n.t(`editor.erase_restore`) }} </template>
        </ActionButton>
      </template>
      <!-- Effects Button -->
      <template>
        <ActionButtonPlaceholder v-if="props.isProcessing"></ActionButtonPlaceholder>
        <ActionButton v-else @click="props.togglePanel('fx')">
          <template #icon>
            <FxIcon />
          </template>
          <template #title>{{ I18n.t(`editor.apply_effects`) }}</template>
        </ActionButton>
      </template>
      <!-- Adjust Button -->
      <template v-if="canvaEditorMvpEnabled">
        <ActionButtonPlaceholder v-if="props.isProcessing"></ActionButtonPlaceholder>
        <ActionButton v-else @click="props.togglePanel('adjust')">
          <template #icon>
            <IconAdjust />
          </template>
          <template #title>{{ I18n.t(`editor.adjust`) }}</template>
        </ActionButton>
      </template>
      <!-- Design Button -->
      <template v-if="canvaEditorMvpEnabled">
        <ActionButtonPlaceholder v-if="props.isProcessing"></ActionButtonPlaceholder>
        <ActionButton v-else @click="props.togglePanel('design')">
          <template #icon>
            <IconDesign />
          </template>
          <template #title>{{ I18n.t(`editor.design`) }}</template>
        </ActionButton>
      </template>
      <template v-else>
        <ActionButtonPlaceholder v-if="props.isProcessing"></ActionButtonPlaceholder>
        <CanvaCta
          v-else
          :getDataURL="props.getDataURL"
          :stage="props.stage"
          :stageHd="props.stageHd"
          :image="store.selectedImage"
          :reset-zoom="props.resetZoom"
          :refresh-all-hd-layers="props.refreshAllHdLayers"
          :persistent-store="props.currentPersistentStore"
        />
      </template>
    </div>
    <!-- Start Error -->
    <div v-if="!isNewPhotoEditor && store.hasError" class="z-5 absolute top-2 w-[328px]">
      <Error />
    </div>
    <!-- End Error -->
    <!-- End Buttons -->
  </div>
</template>

<script setup lang="ts">
import "@/src/i18n";
import { Panel, useEditorStore } from "@/stores/editor_store";
import { PersistentStore } from "@/stores/persistent_store";
import { useMonitorSize } from "@/composables/monitor_size";
import { ActionButton, ActionButtonPlaceholder, BrushIcon, AddIcon, FxIcon } from "prism";
import PanelManager from "../panel_manager.vue";
import { computed, inject, StyleValue } from "vue";
import { hasExceededMagicBrushIterations, Image } from "@/modules/internal_api/image";
import { Stage } from "konva/lib/Stage";
import CanvaCta from "@/components/canva_cta.vue";
import Error from "@/components/upload/error.vue";
import Flipper from "@/modules/flipper";
import IconAdjust from "./side_panel_icons/icon-adjust.vue";
import IconDesign from "./side_panel_icons/icon-design.vue";
import { I18n as I18nType } from "i18n-js";
import { usePhotoEditor } from "@/composables/photo_editor";

const I18n: I18nType = inject("I18n");

interface SidePanelProps {
  currentPersistentStore: PersistentStore | null;
  isProcessing: boolean;
  backgroundActionButtonStyle: StyleValue;
  scale: number;
  stage: Stage;
  stageHd: Stage;
  credits: number;
  editor: HTMLElement;
  initialScale: number;
  togglePanel: (id: Panel) => void;
  refreshTippy: () => void;
  loadCreditsAndUpdatePreview: () => Promise<void>;
  getDataURL: (image: Image, stage: any, element: any) => string;
  resetZoom: () => void;
  refreshAllHdLayers: (image: Image) => Promise<void>;
}

const props = defineProps<SidePanelProps>();
const { isSmallTablet } = useMonitorSize();

const store = useEditorStore();

const { isNewEditorEnabled: isNewPhotoEditor } = usePhotoEditor();

const restoreOriginalResult = (): void => {
  props.currentPersistentStore?.reset();
};

const isNewDesign = computed(() => {
  return store.useNewEditor(isSmallTablet.value);
});

const enableUI = computed(() => {
  return !store.hasError && store.openedPanel === null && !isNewDesign.value;
});

const panelWidth = computed(() => {
  if (isNewDesign.value) {
    return store.openedPanel !== null ? "18rem" : "0";
  }
  return "24rem";
});

const panelManagerWrapperStyles = computed(() => {
  return {
    "z-5 absolute top-0": !isNewPhotoEditor.value,
    "panel-wrapper": isNewPhotoEditor.value,
  };
});

const canvaEditorMvpEnabled = computed(() => {
  return Flipper.isEnabled("canva_in_the_editor");
});

const smallTabletBreakpoint = computed(() => {
  return isSmallTablet.value ? "hidden" : "flex";
});
</script>
<style scoped>
.panel-wrapper {
  @media (min-width: 1024px) {
    max-width: 18rem;
  }
}
</style>
